import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { customerAPI, paymentAPI, nasAPI, broadbandAPI, oltAPI, agentAppAPI, gtplSaathiAPI } from '../services/api';
import { customerTypeLabel } from '../utils/customerTypeLabel';
import { useAuth } from '../context/AuthContext';
import { format, differenceInDays, startOfMonth, endOfMonth } from 'date-fns';
import {
  Search, Plus, Eye, Edit2, Phone, MapPin, Upload, Trash2,
  Activity, X, Save, Download, AlertTriangle, MoreVertical, ExternalLink,
  CheckCircle2, PauseCircle, XCircle, Clock, Wallet, Receipt, MessageCircle, AlertCircle,
  SlidersHorizontal, MoreHorizontal,
} from 'lucide-react';
import { useViewMode } from '../hooks/useViewMode';
import { AppSheet, SheetRow } from '../components/common/AppViewKit';
import toast from 'react-hot-toast';
import { LiveTrafficBadge, LiveBandwidthPanel } from '../components/LiveBandwidth';
import ResponsiveTable from '../components/common/ResponsiveTable';
import SearchableSelect from '../components/common/SearchableSelect';
import useDarkReady from '../theme/useDarkReady';

const STATUS_COLORS = { active: 'success', inactive: 'danger', suspended: 'warning' };
const TYPE_COLORS   = { broadband: 'broadband', catv: 'catv', both: 'both' };

const inp = {
  width: '100%', padding: '7px 10px', border: '1px solid var(--border, #d1d5db)',
  borderRadius: 6, fontSize: '.85rem', boxSizing: 'border-box',
  background: 'var(--input-bg, #fff)', color: 'var(--text)',
};
const lbl = { fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3, display: 'block' };

function IspVlanCell({ customerId, ispName, broadband, onSaved }) {
  const [editing,  setEditing] = useState(false);
  const [saving,   setSaving]  = useState(false);
  const [value,    setValue]   = useState(ispName);
  const ref = useRef(null);

  useEffect(() => {
    if (!editing) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) handleSave(value); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [editing, value]); // eslint-disable-line

  const handleSave = async (val) => {
    if (val === ispName) { setEditing(false); return; }
    setSaving(true);
    try {
      await customerAPI.update(customerId, { broadband: { ...broadband, ispName: val } });
      toast.success('ISP updated');
      onSaved();
    } catch {
      toast.error('Failed to update ISP');
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <div ref={ref} style={{ display: 'flex', gap: 4, alignItems: 'center', minWidth: 110 }}>
        <input
          autoFocus
          placeholder="ISP name"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSave(value); if (e.key === 'Escape') setEditing(false); }}
          disabled={saving}
          style={{ fontSize: '.78rem', padding: '3px 6px', border: '1px solid var(--primary)', borderRadius: 4, flex: 1, minWidth: 0 }}
        />
        <button onClick={() => handleSave(value)} disabled={saving}
          style={{ fontSize: '.72rem', padding: '2px 6px', background: 'var(--primary)', color: 'var(--c-ffffff)', border: 'none', borderRadius: 4, cursor: 'pointer', flexShrink: 0 }}>
          {saving ? '…' : 'Save'}
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={() => { setValue(ispName); setEditing(true); }}
      title="Click to edit ISP"
      style={{ fontSize: '.78rem', cursor: 'pointer', borderRadius: 4, padding: '2px 4px', transition: 'background .12s' }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-light, #eff6ff)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      {ispName
        ? <span style={{ fontWeight: 600, color: 'var(--text)' }}>{ispName}</span>
        : <span style={{ color: 'var(--text-muted)' }}>— <Edit2 size={9} style={{ opacity: 0.4 }} /></span>
      }
    </div>
  );
}

function AreaCell({ customerId, currentArea, areas, onSaved }) {
  const [editing,  setEditing]  = useState(false);
  const [addNew,   setAddNew]   = useState(false);
  const [newValue, setNewValue] = useState('');
  const [saving,   setSaving]   = useState(false);
  const newInputRef = useRef(null);

  const save = async (newArea) => {
    const trimmed = newArea.trim();
    if (!trimmed || trimmed === currentArea) { setEditing(false); setAddNew(false); return; }
    setSaving(true);
    try {
      await customerAPI.update(customerId, { 'address.area': trimmed });
      toast.success('Area updated');
      onSaved();
    } catch {
      toast.error('Failed to update area');
    } finally {
      setSaving(false);
      setEditing(false);
      setAddNew(false);
      setNewValue('');
    }
  };

  const handleSelect = (e) => {
    const val = e.target.value;
    if (val === '__add_new__') {
      setAddNew(true);
      setNewValue('');
      setTimeout(() => newInputRef.current?.focus(), 0);
    } else {
      save(val);
    }
  };

  if (editing) {
    if (addNew) {
      return (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', minWidth: 130 }}>
          <input
            ref={newInputRef}
            value={newValue}
            onChange={e => setNewValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') save(newValue);
              if (e.key === 'Escape') { setAddNew(false); setEditing(false); }
            }}
            placeholder="New area name"
            disabled={saving}
            style={{
              fontSize: '.78rem', padding: '3px 6px', border: '1px solid var(--primary)',
              borderRadius: 4, flex: 1, minWidth: 0,
            }}
          />
          <button
            onClick={() => save(newValue)}
            disabled={saving || !newValue.trim()}
            style={{
              fontSize: '.72rem', padding: '2px 6px', background: 'var(--primary)',
              color: 'var(--c-ffffff)', border: 'none', borderRadius: 4, cursor: 'pointer', flexShrink: 0,
            }}
          >
            {saving ? '…' : 'Add'}
          </button>
          <button
            onClick={() => { setAddNew(false); setEditing(false); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, flexShrink: 0 }}
          >
            <X size={13} />
          </button>
        </div>
      );
    }

    return (
      <select
        autoFocus
        defaultValue={currentArea}
        disabled={saving}
        onChange={handleSelect}
        onBlur={e => { if (e.target.value !== '__add_new__') save(e.target.value); }}
        style={{
          fontSize: '.8rem', padding: '3px 6px', border: '1px solid var(--primary)',
          borderRadius: 4, background: 'var(--bg-card)', color: 'var(--text)',
          minWidth: 110, cursor: 'pointer',
        }}
      >
        <option value="">— none —</option>
        {areas.map(a => <option key={a} value={a}>{a}</option>)}
        {currentArea && !areas.includes(currentArea) && (
          <option value={currentArea}>{currentArea}</option>
        )}
        <option value="__add_new__">+ Add new area…</option>
      </select>
    );
  }

  return (
    <div
      onClick={() => { setEditing(true); setAddNew(false); }}
      title="Click to edit area"
      style={{
        fontSize: '.82rem', display: 'flex', alignItems: 'center', gap: 4,
        cursor: 'pointer', borderRadius: 4, padding: '2px 4px',
        transition: 'background .12s',
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-light, #eff6ff)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <MapPin size={11} color="var(--text-muted)" />
      {currentArea || <span style={{ color: 'var(--text-muted)' }}>—</span>}
      <Edit2 size={10} color="var(--text-muted)" style={{ opacity: 0.5, marginLeft: 2 }} />
    </div>
  );
}

function QuickEditModal({ customer, areas, onSave, onClose }) {
  const isBB = customer.customerType === 'broadband' || customer.customerType === 'both';
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name:       customer.name || '',
    mobile:     customer.mobile || '',
    status:     customer.status || 'active',
    area:       customer.address?.area || '',
    username:   customer.broadband?.username || '',
    plan:       customer.broadband?.plan?._id || customer.broadband?.plan || '',
    expiryDate: customer.broadband?.expiryDate
      ? new Date(customer.broadband.expiryDate).toISOString().split('T')[0]
      : '',
  });

  useEffect(() => {
    if (isBB) broadbandAPI.getPlans({ activeOnly: 'false' }).then(r => setPlans(r.data?.data || [])).catch(() => {});
  }, [isBB]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name:    form.name,
        mobile:  form.mobile,
        status:  form.status,
        address: { ...customer.address, area: form.area },
      };
      if (isBB) {
        payload.broadband = {
          ...customer.broadband,
          username:   form.username,
          plan:       form.plan || customer.broadband?.plan?._id || customer.broadband?.plan,
          expiryDate: form.expiryDate || undefined,
        };
      }
      await customerAPI.update(customer._id, payload);
      toast.success('Customer updated');
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--card-bg, #fff)', borderRadius: 12,
        width: '100%', maxWidth: 500, margin: '0 16px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px', borderBottom: '1px solid var(--border)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Edit2 size={15} color="var(--primary)" />
            <span style={{ fontWeight: 700, fontSize: '.95rem' }}>Quick Edit</span>
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>— {customer.name}</span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {customer.billingNumber && (
            <div style={{ background: 'var(--cb-f8fafc)', borderRadius: 8, padding: '10px 14px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)' }}>BILLING NO.</span>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1rem', color: 'var(--text)', letterSpacing: 1 }}>
                {customer.billingNumber}
              </span>
            </div>
          )}
          {isBB && (
            <div style={{ background: 'var(--primary-light, #eff6ff)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--primary, var(--primary))22' }}>
              <label style={{ ...lbl, color: 'var(--primary)', fontSize: '.78rem' }}>PPPoE Username</label>
              <input style={{ ...inp, fontFamily: 'monospace', fontSize: '.9rem', fontWeight: 600 }}
                value={form.username} onChange={e => set('username', e.target.value)}
                placeholder="e.g. angel_001" autoFocus />
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div><label style={lbl}>Name</label><input style={inp} value={form.name} onChange={e => set('name', e.target.value)} /></div>
            <div><label style={lbl}>Mobile</label><input style={inp} value={form.mobile} onChange={e => set('mobile', e.target.value)} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={lbl}>Status</label>
              <select style={inp} value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div>
              <label style={lbl}>Area</label>
              <select style={inp} value={form.area} onChange={e => set('area', e.target.value)}>
                <option value="">— select area —</option>
                {areas.map(a => <option key={a} value={a}>{a}</option>)}
                {form.area && !areas.includes(form.area) && <option value={form.area}>{form.area}</option>}
              </select>
            </div>
          </div>
          {isBB && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <label style={lbl}>Broadband Plan</label>
                <SearchableSelect
                  value={form.plan}
                  onChange={v => set('plan', v)}
                  placeholder="Search plan…"
                  options={plans.map(p => ({ value: p._id, label: p.displayName || p.name }))}
                />
              </div>
              <div>
                <label style={lbl}>Expiry Date</label>
                <input type="date" style={inp} value={form.expiryDate} onChange={e => set('expiryDate', e.target.value)} />
              </div>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          padding: '12px 18px', borderTop: '1px solid var(--border)',
          background: 'var(--bg, #f9fafb)',
        }}>
          <button className="btn btn-secondary btn-sm" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>
            {saving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : <Save size={13} />}
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Row context menu
function RowMenu({ customer, onEdit, onDelete, onLive, isBB, isAdmin }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 6px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--text-muted)' }}>
        <MoreVertical size={14} />
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 100,
          background: 'var(--card-bg, #fff)', border: '1px solid var(--border)',
          borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,.12)', minWidth: 140,
        }}>
          <button onClick={() => { onEdit(); setOpen(false); }}
            style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '.83rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Edit2 size={12} /> Quick Edit
          </button>
          {isBB && customer.broadband?.username && (
            <button onClick={() => { onLive(); setOpen(false); }}
              style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '.83rem', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--accent)' }}>
              <Activity size={12} /> Live Bandwidth
            </button>
          )}
          {isAdmin && (
            <button onClick={() => { onDelete(); setOpen(false); }}
              style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '.83rem', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--c-dc2626)', borderTop: '1px solid var(--border)' }}>
              <Trash2 size={12} /> Delete
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function CustomersPage() {
  // App View (bottom-tab shell): All/CableTV/Broadband switch, search + Filters button (the
  // filter bar opens in a sheet), and compact 3-line cards with a ⋯ action sheet.
  const { viewMode } = useViewMode();
  const isApp = viewMode === 'app';
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appActions, setAppActions] = useState(null); // customer whose ⋯ sheet is open
  useDarkReady();
  const [searchParams] = useSearchParams();
  // Passed as router state to the detail page so its "Back to Customers" returns to this
  // exact filtered view (e.g. ?customerType=catv) instead of always resetting to the
  // unfiltered list.
  const returnTo = `/customers${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search:       searchParams.get('search')       || '',
    status:       searchParams.get('status')       || '',
    customerType: searchParams.get('customerType') || '',
    area:         searchParams.get('area')         || '',
    node:           '',
    expiringIn:     '',
    isp:            '',
    ponFilter:      '',
    balance:      searchParams.get('balance')      || '',
    newThisMonth: searchParams.get('newThisMonth') || '',
    durationType:   '',
    page: 1,
    limit: 50,
  });
  const [areas, setAreas] = useState([]);
  const [ispOptions, setIspOptions] = useState([]);
  const [nodeOptions, setNodeOptions] = useState([]);
  const [oltPortMap,    setOltPortMap]    = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [liveRates, setLiveRates] = useState({});
  const liveTimerRef = useRef(null);
  const [bwPopup,  setBwPopup]  = useState(null);
  const [ponPopup, setPonPopup] = useState(null); // { customer, onu }
  const [editCustomer, setEditCustomer] = useState(null);
  // Defaults to billing number, ascending — matches the server's default sort (see
  // fetchCustomers' explicit re-sort below) and gives the column a visible active state
  // instead of leaving the user with no way to see or get back to that order once they've
  // clicked a different column header.
  const [sort, setSort] = useState({ key: 'billing', dir: 1 }); // dir: 1 = ascending, -1 = descending
  const [pendingReasons, setPendingReasons] = useState({}); // customerId -> { reason, recordedAt, agent }
  const [reasonLabels, setReasonLabels] = useState({}); // reason key -> display label
  const { isAdmin, hasModuleRight } = useAuth();
  const canSuspendGtplActive = hasModuleRight('gtplSaathi', 'suspendActive');
  // customerId -> true while a quick GTPL suspend/activate toggle is mid-request, so only
  // that row's switch shows "Submitting…" instead of the whole table re-rendering as busy.
  const [togglingGtplId, setTogglingGtplId] = useState(null);
  const navigate = useNavigate();

  const ponPortOptions = useMemo(() => {
    const ports = new Set();
    Object.values(oltPortMap).forEach(onu => { if (onu.port != null) ports.add(String(onu.port)); });
    return [...ports].sort((a, b) => {
      const na = parseFloat(a), nb = parseFloat(b);
      return (!isNaN(na) && !isNaN(nb)) ? na - nb : a.localeCompare(b);
    });
  }, [oltPortMap]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    try {
      const params = { ...filters };
      // client-side filters — remove from server params
      delete params.isp;
      delete params.ponFilter;
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const { data } = await customerAPI.getAll(params);
      let docs = data.data;
      // Arrangement always follows the billing number sequence (server sorts the same way)
      docs.sort((a, b) => (a.billingNumber ?? Infinity) - (b.billingNumber ?? Infinity));
      // ISP filter — broadband only
      if (filters.customerType === 'broadband' && filters.isp) {
        docs = docs.filter(c => c.broadband?.ispName === filters.isp);
      }
      // PON filter — broadband only
      if (filters.customerType === 'broadband' && filters.ponFilter) {
        docs = docs.filter(c => {
          const onu = oltPortMap[(c.broadband?.username || '').toLowerCase().trim()];
          if (filters.ponFilter === 'has') return !!onu;
          if (filters.ponFilter === 'no')  return !onu;
          if (filters.ponFilter.startsWith('port:')) return onu && String(onu.port) === filters.ponFilter.slice(5);
          return true;
        });
      }
      setCustomers(docs);
      setPagination(data.pagination);
    } catch {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [filters, oltPortMap]);

  // Same quick Suspend/Activate toggle as Customer Detail's Hardware Details card — real
  // gtplsaathi.com call + Customer.catv.gtplSuspended kept in sync together (see that field's
  // own comment on Customer.js). Patches the row in place rather than a full refetch.
  const handleToggleGtplSuspend = async (c) => {
    const stb = c.catv?.setTopBoxSerial;
    const accountNumber = c.catv?.accountNumber;
    if (!stb && !accountNumber) return;
    const nextAction = c.catv?.gtplSuspended ? 'ACTIVE' : 'SUSPEND';
    setTogglingGtplId(c._id);
    try {
      const { data } = await gtplSaathiAPI.suspendActive({
        stb, accountNumber, action: nextAction,
        reason: nextAction === 'ACTIVE' ? 'Payment Received' : 'Payment Due',
        customerId: c._id,
      });
      if (data.queued) {
        toast(data.message, { icon: '⏳' });
      } else {
        toast.success(nextAction === 'ACTIVE' ? 'Activated on GTPL Saathi' : 'Suspended on GTPL Saathi');
        setCustomers(list => list.map(x => x._id === c._id
          ? { ...x, catv: { ...x.catv, gtplSuspended: nextAction === 'SUSPEND' } }
          : x));
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Suspend/Activate failed');
    } finally {
      setTogglingGtplId(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete customer "${name}"? This cannot be undone.`)) return;
    try {
      await customerAPI.delete(id);
      toast.success('Customer deleted');
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete customer');
    }
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (!window.confirm(`Delete ${ids.length} selected customer(s)? This cannot be undone.`)) return;
    try {
      await customerAPI.deleteMany(ids);
      toast.success(`${ids.length} customer(s) deleted`);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete customers');
    }
  };

  // No bulk-status backend route — loops the same PUT /:id/status each row-level toggle
  // already uses, so per-customer audit logging (ChangeLog) still fires for every one.
  const [bulkStatusBusy, setBulkStatusBusy] = useState(false);
  const handleDeactivateSelected = async () => {
    const ids = [...selectedIds];
    if (!window.confirm(`Mark ${ids.length} selected customer(s) as Inactive?`)) return;
    setBulkStatusBusy(true);
    let ok = 0, failed = 0;
    for (const id of ids) {
      try { await customerAPI.updateStatus(id, { status: 'inactive' }); ok++; }
      catch { failed++; }
    }
    setBulkStatusBusy(false);
    setSelectedIds(new Set());
    toast[failed ? 'error' : 'success'](`Deactivated ${ok}${failed ? `, ${failed} failed` : ''}`);
    fetchCustomers();
  };

  const toggleSelect    = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => selectedIds.size === customers.length ? setSelectedIds(new Set()) : setSelectedIds(new Set(customers.map(c => c._id)));

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
  useEffect(() => {
    const params = filters.customerType === 'catv' ? { customerType: 'catv,both' }
      : filters.customerType === 'broadband' ? { customerType: 'broadband,both' }
      : undefined;
    customerAPI.getAreas(params).then(({ data }) => setAreas(data.data)).catch(() => {});
  }, [filters.customerType]);
  useEffect(() => {
    agentAppAPI.getCollectionPendingReasons().then(({ data }) => {
      const map = {};
      (data.data || []).forEach(r => { map[r.key] = r.label; });
      setReasonLabels(map);
    }).catch(() => {});
  }, []);
  // Pending-collection badge — CableTV list only, one place to see why a payment wasn't collected
  useEffect(() => {
    if (filters.customerType !== 'catv' || customers.length === 0) { setPendingReasons({}); return; }
    paymentAPI.getCollectionPendingLatest(customers.map(c => c._id))
      .then(({ data }) => setPendingReasons(data.data || {}))
      .catch(() => {});
  }, [customers, filters.customerType]);
  // Sidebar links (e.g. Customers > CableTV / Broadband) navigate to /customers with a
  // different ?customerType= without unmounting this page, so pick up later changes here too.
  useEffect(() => {
    const qt = searchParams.get('customerType') || '';
    const nm = searchParams.get('newThisMonth') || '';
    setFilters(f => (f.customerType === qt && f.newThisMonth === nm ? f : {
      ...f, customerType: qt, newThisMonth: nm, page: 1,
      ...(qt !== 'broadband' ? { isp: '', ponFilter: '' } : {}),
      ...(qt !== 'catv' ? { node: '', balance: '', durationType: '' } : {}),
    }));
  }, [searchParams]);
  useEffect(() => {
    // Real distinct broadband.ispName values already in use across customers — not the
    // separate admin-curated "ispProfiles" AppOptions list, which can drift out of sync
    // (e.g. still listing an old ISP name after customer records were renamed).
    customerAPI.getIspNames().then(r => setIspOptions(r.data?.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    // Real distinct node values already in use across customers — not the separate
    // admin-curated "nodes" AppOptions list (which the Add/Edit Customer forms' Node
    // AddableSelect uses instead, merged with this same real-data source).
    customerAPI.getNodes().then(r => setNodeOptions(r.data?.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    oltAPI.getONUs().then(r => {
      const onus = r.data?.data || [];
      const map = {};
      for (const o of onus) {
        const label = (o.label || o.description || '').toLowerCase().trim();
        if (label) map[label] = {
          port:             o.port             ?? o.ponPort      ?? null,
          serialNumber:     o.serialNumber     ?? null,
          model:            o.model            ?? null,
          status:           o.status           ?? null,
          rxPower:          o.rxPower          ?? null,
          gponId:           o.id               ?? null,
          lastDeregister:   o.lastDeregister   ?? null,
          deregisterReason: o.deregisterReason ?? null,
        };
      }
      setOltPortMap(map);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchLive = () => nasAPI.getAllLive().then(r => setLiveRates(r.data.data || {})).catch(() => {});
    fetchLive();
    liveTimerRef.current = setInterval(fetchLive, 2_000);
    return () => clearInterval(liveTimerRef.current);
  }, []);

  // Download CSV
  const downloadCSV = () => {
    const headers = ['S.CODE', 'NAME', 'MOBILE', 'TYPE','STATUS', 'AREA', 'STB/USERNAME', 'CARD/ACCOUNT', 'BALANCE', 'PLAN', 'PLAN PRICE', 'BB EXPIRY', 'CATV EXPIRY'];
    const rows = customers.map(c => [
      c.customerId,
      c.name,
      c.mobile || '',
      c.customerType,
      c.status,
      c.address?.area || '',
      c.catv?.setTopBoxSerial || c.broadband?.username || '',
      c.catv?.accountNumber || c.catv?.entityCode || '',
      c.outstandingBalance || 0,
      c.broadband?.plan?.name || c.catv?.package?.name || '',
      c.broadband?.plan?.price || c.catv?.package?.price || '',
      c.broadband?.expiryDate ? format(new Date(c.broadband.expiryDate), 'dd-MMM-yyyy') : '',
      c.catv?.expiryDate     ? format(new Date(c.catv.expiryDate),      'dd-MMM-yyyy') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customers_${format(new Date(), 'dd-MMM-yyyy')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Monthly Google Contacts sync — one CSV per service (mirrors the LCV_MAY26.csv /
  // AIS_MAY26.csv files the owner used to build by hand). Always the full active-customer
  // list for the service unless rows are checked in the table, in which case it exports
  // exactly those (ignores active/date filters). Google Contacts import matches/updates
  // existing contacts, so re-sending everyone each time is safe. The filename itself
  // (LCV_JUL26.csv / AIS_JUL26.csv) is what contacts.google.com's import dialog shows.
  const downloadContacts = async (service) => {
    try {
      const ids = [...selectedIds];
      const { data, headers } = await customerAPI.exportContacts(ids.length ? { service, ids: ids.join(',') } : { service });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${service === 'catv' ? 'LCV' : 'AIS'}_${format(new Date(), 'MMMyy').toUpperCase()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      const count = Number(headers?.['x-contacts-count'] ?? 0);
      const scope = ids.length ? `${ids.length} selected` : 'all active';
      toast.success(`${count} contact${count === 1 ? '' : 's'} (${scope}) — import it at contacts.google.com`);
    } catch (err) {
      toast.error('Contacts export failed');
    }
  };

  const getExpiryDate = (c) => {
    if (c.customerType === 'broadband') return c.broadband?.expiryDate;
    if (c.customerType === 'catv')      return c.catv?.expiryDate;
    // both: pick the one that's closer or earlier
    const bb = c.broadband?.expiryDate ? new Date(c.broadband.expiryDate) : null;
    const cv = c.catv?.expiryDate      ? new Date(c.catv.expiryDate)      : null;
    if (!bb && !cv) return null;
    if (!bb) return c.catv.expiryDate;
    if (!cv) return c.broadband.expiryDate;
    return bb < cv ? c.broadband.expiryDate : c.catv.expiryDate;
  };

  const expiryChip = (date) => {
    if (!date) return <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>—</span>;
    const d = differenceInDays(new Date(date), new Date());
    const formatted = format(new Date(date), 'dd/MM/yy');
    if (d < 0)  return <span style={{ color: 'var(--c-dc2626)', fontWeight: 700, fontSize: '.8rem' }}>{formatted}</span>;
    if (d === 0) return <span style={{ color: 'var(--c-dc2626)', fontWeight: 700, fontSize: '.8rem' }}>{formatted}</span>;
    if (d <= 3)  return <span style={{ color: 'var(--c-d97706)', fontWeight: 600, fontSize: '.8rem' }}>{formatted}</span>;
    if (d <= 7)  return <span style={{ color: 'var(--primary)', fontWeight: 600, fontSize: '.8rem' }}>{formatted}</span>;
    return <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>{formatted}</span>;
  };

  const isExpired = (c) => {
    const d = getExpiryDate(c);
    if (!d) return false;
    return new Date(d) < new Date();
  };

  // ── Column sorting (client-side, current page) ──
  const toggleSort = (key) => setSort(s => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const sortValue = (c, key) => {
    switch (key) {
      case 'pon': {
        const onu = oltPortMap[(c.broadband?.username || '').toLowerCase().trim()];
        return onu?.port ?? null;
      }
      case 'hw': return filters.customerType === 'catv'
        ? (c.catv?.setTopBoxSerial || c.catv?.accountNumber || null)
        : (c.broadband?.username || c.catv?.accountNumber || null);
      case 'billing': return c.billingNumber ?? null;
      case 'name':    return c.name || null;
      case 'isp':     return c.broadband?.ispName || null;
      case 'area':    return c.address?.area || null;
      case 'houseno': {
        const raw = c.broadband?.houseNumber || c.catv?.houseNumber || null;
        // "4-F" sequences with "4" (same unit, not a separate one) — strip the suffix
        // before the numeric compare below runs.
        return raw ? raw.replace(/-F$/i, '').trim() : null;
      }
      case 'node':    return c.catv?.node || null;
      case 'billamt': return c.broadband?.plan?.price ?? c.catv?.package?.price ?? null;
      case 'expired': { const d = getExpiryDate(c); return d ? new Date(d).getTime() : null; }
      case 'status':  return c.status || null;
      default: return null;
    }
  };

  const sortedCustomers = !sort.key ? customers : [...customers].sort((a, b) => {
    const va = sortValue(a, sort.key), vb = sortValue(b, sort.key);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;  // empty values always last, regardless of direction
    if (vb == null) return -1;
    if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sort.dir;
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' }) * sort.dir;
  });

  const SortTh = ({ k, children, style, ...rest }) => (
    <th {...rest} onClick={() => toggleSort(k)} title="Click to sort"
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...(style || {}) }}>
      {children}
      <span style={{ marginLeft: 4, fontSize: '.65rem', opacity: sort.key === k ? 1 : 0.35 }}>
        {sort.key === k ? (sort.dir === 1 ? '▲' : '▼') : '↕'}
      </span>
    </th>
  );

  // Per-row derived values shared across several columns' render() calls
  const rowMeta = (c) => {
    const isBB   = c.customerType === 'broadband' || c.customerType === 'both';
    const isCatv = c.customerType === 'catv'      || c.customerType === 'both';
    const liveRate = isBB && c.broadband?.username
      ? liveRates[c.broadband.username.toLowerCase()] : null;
    const planPrice = isBB ? c.broadband?.plan?.price : c.catv?.package?.price;
    return { isBB, isCatv, expired: isExpired(c), expDate: getExpiryDate(c), liveRate, planPrice };
  };

  const columns = [
    isAdmin && {
      key: 'checkbox', label: 'Select', width: 36, titleLeft: true,
      headerRender: () => <input type="checkbox" checked={customers.length > 0 && selectedIds.size === customers.length} onChange={toggleSelectAll} />,
      render: c => <input type="checkbox" checked={selectedIds.has(c._id)} onChange={() => toggleSelect(c._id)} />,
    },
    filters.customerType === 'catv' && {
      key: 'collect', label: 'COLLECT', width: 100,
      render: c => (
        <>
          <div
            title="Outstanding balance"
            style={{ fontWeight: 700, fontSize: '.82rem', marginBottom: 4, color: (c.outstandingBalance || 0) > 0 ? 'var(--c-dc2626)' : 'var(--c-16a34a)' }}
          >
            ₹ {(c.outstandingBalance || 0).toLocaleString('en-IN')}
          </div>
          <button
            onClick={() => navigate(`/customers/${c._id}?tab=collect`, { state: { from: returnTo } })}
            title="Collect Payment"
            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 6, border: 'none', background: 'var(--cb-16a34a)', color: 'var(--c-ffffff)', fontSize: '.78rem', fontWeight: 600, cursor: 'pointer' }}
          >
            <Wallet size={12} /> Collect
          </button>
          {pendingReasons[c._id] && (
            <div
              title={`Pending: ${reasonLabels[pendingReasons[c._id].reason] || pendingReasons[c._id].reason}${pendingReasons[c._id].agent ? ` · by ${pendingReasons[c._id].agent}` : ''}${pendingReasons[c._id].recordedAt ? ` · ${format(new Date(pendingReasons[c._id].recordedAt), 'dd-MMM-yyyy HH:mm')}` : ''}`}
              style={{ marginTop: 5, fontSize: '.7rem', fontWeight: 600, color: 'var(--c-b45309)', maxWidth: 110 }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
                <AlertCircle size={11} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {reasonLabels[pendingReasons[c._id].reason] || pendingReasons[c._id].reason}
                </span>
              </div>
              {pendingReasons[c._id].recordedAt && (
                <div style={{ fontWeight: 500, color: 'var(--c-92400e)', marginTop: 1 }}>
                  {format(new Date(pendingReasons[c._id].recordedAt), 'dd-MMM HH:mm')}
                </div>
              )}
            </div>
          )}
        </>
      ),
    },
    filters.customerType === 'broadband' && {
      key: 'pon', label: 'PON',
      headerRender: () => <SortTh k="pon">PON</SortTh>,
      render: c => {
        if (!c.broadband?.username) return <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>—</span>;
        const onu = oltPortMap[c.broadband.username.toLowerCase()];
        return (
          <div>
            <button
              onClick={() => onu ? setPonPopup({ customer: c, onu }) : null}
              style={{ fontFamily: 'monospace', fontSize: '.85rem', fontWeight: 700, color: onu ? 'var(--accent)' : 'var(--text-muted)', background: 'none', border: 'none', padding: 0, cursor: onu ? 'pointer' : 'default', textDecoration: 'none' }}
              title={onu ? 'View hardware details' : 'No OLT match'}
            >
              {onu ? `PON ${onu.port}` : '—'}
            </button>
            <div style={{ marginTop: 4 }}>
              <Link
                to={`/olt?serial=${encodeURIComponent(c.broadband.username)}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '.7rem', color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                title="Open in OLT"
              >
                <ExternalLink size={10} /> OLT
              </Link>
            </div>
          </div>
        );
      },
    },
    {
      key: 'hw',
      label: filters.customerType === 'broadband' ? 'PPPoE' : filters.customerType === 'catv' ? 'HARDWARE / A/C' : 'PPPoE / ACCOUNT',
      headerRender: () => (
        <SortTh k="hw">{filters.customerType === 'broadband' ? 'PPPoE' : filters.customerType === 'catv' ? 'HARDWARE / A/C' : 'PPPoE / ACCOUNT'}</SortTh>
      ),
      render: c => {
        const { isBB, isCatv } = rowMeta(c);
        const showCatvLink = isCatv && filters.customerType === 'catv' && (c.catv?.setTopBoxSerial || c.catv?.accountNumber);
        const showCatvAcct = isCatv && filters.customerType !== 'catv' && c.catv?.accountNumber;
        if (!c.broadband?.username && !showCatvLink && !showCatvAcct) {
          return <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>—</span>;
        }
        return (
          <>
            {isBB && c.broadband?.username && (
              <div>
                <Link
                  to={`/customers/${c._id}`}
                  state={{ from: returnTo }}
                  className="font-mono"
                  style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--primary)', textDecoration: 'none' }}
                  onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                >
                  {c.broadband.username}
                </Link>
                {rowMeta(c).liveRate && (
                  <div style={{ marginTop: 3 }}>
                    <LiveTrafficBadge rate={rowMeta(c).liveRate} />
                  </div>
                )}
              </div>
            )}
            {showCatvLink && (
              <div style={{ marginTop: isBB ? 6 : 0 }}>
                {c.catv?.setTopBoxSerial && (
                  <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '.83rem', color: 'var(--text)' }}>
                    {c.catv.setTopBoxSerial}
                  </div>
                )}
                {c.catv?.accountNumber && (
                  <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    A/C {c.catv.accountNumber}
                  </div>
                )}
                {canSuspendGtplActive && (
                  <div
                    onClick={() => togglingGtplId !== c._id && handleToggleGtplSuspend(c)}
                    role="switch" aria-checked={!c.catv?.gtplSuspended}
                    title="Flip this STB's status on GTPL Saathi"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 4, cursor: togglingGtplId === c._id ? 'default' : 'pointer', userSelect: 'none', opacity: togglingGtplId === c._id ? .6 : 1 }}
                  >
                    <span style={{
                      width: 26, height: 15, borderRadius: 8, position: 'relative', flexShrink: 0,
                      background: c.catv?.gtplSuspended ? 'var(--cb-d1d5db)' : 'var(--cb-16a34a)', transition: 'background .15s',
                    }}>
                      <span style={{
                        position: 'absolute', top: 1.5, left: c.catv?.gtplSuspended ? 1.5 : 12.5, width: 12, height: 12,
                        borderRadius: '50%', background: 'var(--cb-ffffff)', transition: 'left .15s',
                        boxShadow: '0 1px 2px rgba(0,0,0,.25)',
                      }} />
                    </span>
                    <span style={{ fontSize: '.68rem', fontWeight: 700, color: c.catv?.gtplSuspended ? 'var(--c-b91c1c)' : 'var(--c-15803d)' }}>
                      {togglingGtplId === c._id ? 'Submitting…' : (c.catv?.gtplSuspended ? 'Suspended' : 'Active')}
                    </span>
                  </div>
                )}
              </div>
            )}
            {showCatvAcct && (
              <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '.83rem', color: 'var(--primary)', marginTop: isBB ? 6 : 0 }}>
                {c.catv.accountNumber}
              </div>
            )}
          </>
        );
      },
    },
    {
      key: 'billing', label: 'BILL NO', width: 70,
      headerRender: () => <SortTh k="billing">BILL NO</SortTh>,
      render: c => (
        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '.85rem', color: 'var(--text-muted)' }}>
          {c.billingNumber ?? '—'}
        </span>
      ),
    },
    {
      key: 'name', label: 'NAME', title: true,
      headerRender: () => <SortTh k="name" style={{ minWidth: 240 }}>NAME</SortTh>,
      className: undefined,
      render: c => {
        const { expired } = rowMeta(c);
        return (
          <div style={{ minWidth: 240 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {expired && <AlertTriangle size={13} color="var(--c-dc2626)" title="Expired" style={{ flexShrink: 0 }} />}
              <Link to={`/customers/${c._id}`} state={{ from: returnTo }}
                style={{ fontWeight: 700, color: 'var(--primary)', textDecoration: 'none', fontSize: '.9rem' }}
                onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}>
                {c.name}
              </Link>
              <span className={`badge badge-${TYPE_COLORS[c.customerType]}`} style={{ fontSize: '.65rem' }}>
                {customerTypeLabel(c.customerType)}
              </span>
            </div>
            <div style={{ fontSize: '.73rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3, marginTop: 2 }}>
              <Phone size={10} /> {c.mobile}
            </div>
            <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', marginTop: 1 }}>
              {(() => {
                const society = c.address?.area || '';
                const houseNo = c.broadband?.houseNumber || c.catv?.houseNumber || '';
                if (!society && !houseNo) return null;
                return `ADD : ${society}${houseNo ? ` - ${houseNo}` : ''}`;
              })()}
            </div>
          </div>
        );
      },
    },
    filters.customerType === 'broadband' && {
      key: 'isp', label: 'ISP',
      headerRender: () => <SortTh k="isp">ISP</SortTh>,
      render: c => <IspVlanCell customerId={c._id} ispName={c.broadband?.ispName || ''} broadband={c.broadband} onSaved={fetchCustomers} />,
    },
    {
      key: 'area', label: 'AREA', className: 'customers-col-area',
      half: filters.customerType === 'catv',
      headerRender: () => <SortTh k="area" className="customers-col-area">AREA</SortTh>,
      render: c => <AreaCell customerId={c._id} currentArea={c.address?.area || ''} areas={areas} onSaved={fetchCustomers} />,
    },
    {
      key: 'houseno', label: 'HOUSE NO.', className: 'customers-col-houseno', half: true,
      headerRender: () => <SortTh k="houseno" className="customers-col-houseno">HOUSE NO.</SortTh>,
      render: c => {
        const houseNo = c.broadband?.houseNumber || c.catv?.houseNumber || '';
        return houseNo
          ? <span style={{ fontSize: '.83rem', fontWeight: 600 }}>{houseNo}</span>
          : <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>—</span>;
      },
    },
    filters.customerType === 'catv' && {
      key: 'node', label: 'NODE', className: 'customers-col-hardware', half: true,
      headerRender: () => <SortTh k="node" className="customers-col-hardware">NODE</SortTh>,
      render: c => c.catv?.node
        ? <span style={{ fontSize: '.83rem', fontWeight: 600 }}>{c.catv.node}</span>
        : <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>—</span>,
    },
    {
      key: 'billamt', label: 'LAST BILL AMT', className: 'customers-col-billamt', half: true,
      headerRender: () => <SortTh k="billamt" className="customers-col-billamt">LAST BILL AMT</SortTh>,
      render: c => {
        const { isBB, isCatv, planPrice } = rowMeta(c);
        return (
          <>
            {planPrice ? (
              <span style={{ fontWeight: 700, fontSize: '.88rem' }}>₹ {planPrice.toLocaleString('en-IN')}</span>
            ) : (
              <span style={{ color: 'var(--text-muted)', fontSize: '.8rem' }}>—</span>
            )}
            {isBB && c.broadband?.plan?.name && (
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                {c.broadband.plan.name}
                {c.broadband?.additionalPlans?.length > 0 && ` +${c.broadband.additionalPlans.length}`}
              </div>
            )}
            {isCatv && !isBB && c.catv?.package?.name && (
              <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                {c.catv.package.name}
                {c.catv?.additionalPackages?.length > 0 && ` +${c.catv.additionalPackages.length}`}
              </div>
            )}
          </>
        );
      },
    },
    {
      key: 'expired', label: 'EXPIRED', className: 'customers-col-expired', half: true,
      headerRender: () => <SortTh k="expired" className="customers-col-expired">EXPIRED</SortTh>,
      render: c => expiryChip(rowMeta(c).expDate),
    },
    {
      key: 'status', label: 'STATUS',
      headerRender: () => <SortTh k="status">STATUS</SortTh>,
      render: c => {
        const { isBB } = rowMeta(c);
        // Pure-broadband rows: customer.status defaults 'active' at creation regardless of
        // whether the package has ever been renewed, so it reads as "activated" the moment
        // the customer is added. broadband.isActive only flips true on a real renewal payment
        // (or Close/Reopen Connection) — use it here instead. 'suspended' still overrides, since
        // that's a deliberate account-level lock independent of billing. CATV / combo rows keep
        // customer.status (CATV's Close/Reopen toggle is intentionally status-driven).
        const st = c.customerType === 'broadband'
          ? (c.status === 'suspended' ? 'suspended' : (c.broadband?.isActive ? 'active' : 'inactive'))
          : c.status;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {st === 'active' ? <CheckCircle2 size={16} color="var(--c-16a34a)" title="Active" />
              : st === 'suspended' ? <PauseCircle size={16} color="var(--c-d97706)" title="Suspended" />
              : st === 'inactive' ? <XCircle size={16} color="var(--c-dc2626)" title="Inactive" />
              : <Clock size={16} color="var(--c-6b7280)" title={st || '—'} />}
            <button title="Detail" onClick={() => navigate(`/customers/${c._id}`, { state: { from: returnTo } })}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex', color: 'var(--accent)' }}>
              <Eye size={15} />
            </button>
            <button title="Quick Edit" onClick={() => setEditCustomer(c)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex', color: 'var(--text-muted)' }}>
              <Edit2 size={15} />
            </button>
            {isBB && c.broadband?.username && (
              <button title="Live Bandwidth" onClick={() => setBwPopup({ username: c.broadband.username, name: c.name })}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex', color: 'var(--primary)' }}>
                <Activity size={15} />
              </button>
            )}
            {isAdmin && (
              <button title="Delete" onClick={() => handleDelete(c._id, c.name)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 3, display: 'flex', color: 'var(--c-dc2626)' }}>
                <Trash2 size={15} />
              </button>
            )}
          </div>
        );
      },
    },
  ].filter(Boolean);

  const onSearchChange = e => {
    const search = e.target.value;
    // Typing a society's full name (not just picking it from Select Area) should
    // get the same house-number auto-arrange treatment — same reasoning as the
    // Area dropdown's fix (2026-08-05): without also widening the page limit, the
    // arrange silently truncates to whichever 50 happen to load first by billing
    // number. Exact match only (not every keystroke while typing) so it doesn't
    // flip into arrange-mode mid-type.
    const matchedArea = areas.find(a => a.toLowerCase() === search.trim().toLowerCase());
    setFilters(f => ({ ...f, search, page: 1, limit: matchedArea ? 500 : f.limit }));
    if (matchedArea) setSort({ key: 'houseno', dir: 1 });
  };

  const filterBar = (
      <div className="filter-bar customers-filters">
        <div className="search-input-wrapper customers-search">
          <Search size={14} className="search-icon" />
          <input
            className="search-input" placeholder="Search name, mobile, address, ID, STB..."
            value={filters.search} onChange={onSearchChange}
          />
        </div>
        <select className="form-control customers-select" value={filters.area}
          onChange={e => {
            const area = e.target.value;
            // The house-number sort below only reorders whichever page is already loaded —
            // it never fetches further pages itself. Without also raising the page size,
            // a society bigger than one page (default 50) silently truncates the "sequence"
            // to whoever happens to sort first by billing number, hiding the rest until the
            // admin manually flips to the next page (found 2026-08-05: a recently-reactivated
            // customer ranked 53rd of 61 in its society, so it never appeared on page 1 at all).
            setFilters(f => ({ ...f, area, page: 1, limit: area ? 500 : f.limit }));
            // picking a specific society auto-arranges the list by house/flat/tenement number
            if (area) setSort({ key: 'houseno', dir: 1 });
          }}>
          <option value="">Select Area</option>
          {areas.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="form-control customers-select" value={filters.expiringIn || ''}
          onChange={e => setFilters(f => ({ ...f, expiringIn: e.target.value, page: 1 }))}>
          <option value="">All Expiry</option>
          <option value="0">Expiring Today</option>
          <option value="3">Within 3 Days</option>
          <option value="7">Within 7 Days</option>
        </select>
        <select className="form-control customers-select" value={filters.status}
          onChange={e => setFilters(f => ({ ...f, status: e.target.value, page: 1 }))}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="suspended">Suspended</option>
        </select>
        {filters.customerType === 'catv' && (
          <select className="form-control customers-select" value={filters.balance}
            onChange={e => setFilters(f => ({ ...f, balance: e.target.value, page: 1 }))}>
            <option value="">All Balance</option>
            <option value="due">Due</option>
            <option value="advance">Advance</option>
            <option value="clear">Clear</option>
          </select>
        )}
        {filters.customerType === 'broadband' && (
          <select className="form-control customers-select" value={filters.isp}
            onChange={e => setFilters(f => ({ ...f, isp: e.target.value, page: 1 }))}>
            <option value="">All ISP</option>
            {ispOptions.map(isp => <option key={isp} value={isp}>{isp}</option>)}
          </select>
        )}
        {filters.customerType === 'broadband' && (
          <select className="form-control customers-select" value={filters.ponFilter}
            onChange={e => setFilters(f => ({ ...f, ponFilter: e.target.value, page: 1 }))}>
            <option value="">All PON</option>
            <option value="has">Has PON</option>
            <option value="no">No PON</option>
            {ponPortOptions.map(p => <option key={p} value={`port:${p}`}>PON {p}</option>)}
          </select>
        )}
        {filters.customerType === 'catv' && (
          <select className="form-control customers-select" value={filters.node}
            onChange={e => setFilters(f => ({ ...f, node: e.target.value, page: 1 }))}>
            <option value="">All Node</option>
            {nodeOptions.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        )}
        {filters.customerType === 'catv' && (
          <select className="form-control customers-select" value={filters.durationType}
            onChange={e => setFilters(f => ({ ...f, durationType: e.target.value, page: 1 }))}>
            <option value="">Regular, DTD & AMP</option>
            <option value="regular">Regular</option>
            <option value="dtd">DTD</option>
            <option value="amp">AMP</option>
          </select>
        )}
        <select className="form-control customers-select-sm" value={filters.limit}
          onChange={e => setFilters(f => ({ ...f, limit: Number(e.target.value), page: 1 }))}>
          <option value={25}>25/pg</option>
          <option value={50}>50/pg</option>
          <option value={100}>100/pg</option>
          <option value={5000}>All</option>
        </select>
        <button className="btn btn-secondary btn-sm"
          onClick={() => setFilters(f => ({ search: '', status: '', customerType: f.customerType, area: '', node: '', expiringIn: '', isp: '', ponFilter: '', balance: '', durationType: '', page: 1, limit: 50 }))}>
          Reset
        </button>
      </div>
  );

  // ── App View pieces ──
  const activeFilterCount = ['area', 'expiringIn', 'status', 'balance', 'isp', 'ponFilter', 'node', 'durationType']
    .filter(k => filters[k]).length;
  const appTopRow = (
    <div style={{ display: 'grid', gap: 8, marginBottom: 10 }}>
      <div style={{ display: 'flex', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: 3 }}>
        {[['', 'All'], ['catv', 'CableTV'], ['broadband', 'Broadband']].map(([t, label]) => {
          const on = (filters.customerType || '') === t;
          return (
            <button key={label} type="button" aria-pressed={on}
              onClick={() => navigate(t ? `/customers?customerType=${t}` : '/customers', { replace: true })}
              style={{ flex: 1, minHeight: 40, border: 'none', borderRadius: 9, fontWeight: 700, fontSize: '.82rem', cursor: 'pointer', background: on ? 'var(--primary-light)' : 'none', color: on ? 'var(--primary)' : 'var(--text-muted)' }}>
              {label}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <div className="search-input-wrapper" style={{ flex: 1, minWidth: 0 }}>
          <Search size={14} className="search-icon" />
          <input className="search-input" placeholder="Name, mobile, bill no., PPPoE, STB…" value={filters.search} onChange={onSearchChange} style={{ minHeight: 40 }} />
        </div>
        <button type="button" className="btn btn-secondary" onClick={() => setFiltersOpen(true)} style={{ minHeight: 40, whiteSpace: 'nowrap' }}>
          <SlidersHorizontal size={15} /> Filters{activeFilterCount ? ` · ${activeFilterCount}` : ''}
        </button>
      </div>
    </div>
  );
  const appCard = c => {
    const { isBB, isCatv, expDate, liveRate, planPrice } = rowMeta(c);
    const onu = isBB && c.broadband?.username ? oltPortMap[c.broadband.username.toLowerCase()] : null;
    const houseNo = c.broadband?.houseNumber || c.catv?.houseNumber || '';
    const due = c.outstandingBalance || 0;
    const selected = selectedIds.has(c._id);
    return (
      <div key={c._id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 10px', borderBottom: '1px solid var(--border)', background: selected ? 'var(--primary-light)' : undefined }}>
        {isAdmin && (
          <label style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'center', padding: '0 4px', cursor: 'pointer' }}>
            <input type="checkbox" checked={selected} onChange={() => toggleSelect(c._id)} aria-label={`Select ${c.name}`} />
          </label>
        )}
        <button type="button" onClick={() => navigate(`/customers/${c._id}`, { state: { from: returnTo } })}
          style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', padding: 0, textAlign: 'left', cursor: 'pointer', color: 'var(--text)', display: 'grid', gap: 3 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <strong style={{ flex: 1, minWidth: 0, fontSize: '.88rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</strong>
            <span className={`badge badge-${TYPE_COLORS[c.customerType]}`} style={{ fontSize: '.62rem', flexShrink: 0 }}>{customerTypeLabel(c.customerType)}</span>
            <span style={{ fontFamily: 'monospace', fontSize: '.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>#{c.billingNumber ?? '—'}</span>
          </span>
          <span style={{ fontSize: '.74rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            {isBB && c.broadband?.username && (
              <>
                {/* The OLT map arrives a few seconds after the list — show the PON once known. */}
                {onu?.port != null && <span>PON {onu.port}</span>}
                <span style={{ fontFamily: 'monospace' }}>{c.broadband.username}</span>
                {liveRate && <LiveTrafficBadge rate={liveRate} />}
              </>
            )}
            {isCatv && (c.catv?.setTopBoxSerial || c.catv?.accountNumber) && (
              <span style={{ fontFamily: 'monospace' }}>{c.catv.setTopBoxSerial || `A/C ${c.catv.accountNumber}`}</span>
            )}
            {isCatv && c.catv?.package?.name && <span>{c.catv.package.name}</span>}
          </span>
          <span style={{ fontSize: '.74rem', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <span style={{ flex: 1, minWidth: 0, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {[c.address?.area, houseNo].filter(Boolean).join(' - ') || c.mobile}
            </span>
            {expiryChip(expDate)}
            {isCatv && due > 0
              ? <strong style={{ color: 'var(--c-dc2626)' }}>₹{due.toLocaleString('en-IN')}</strong>
              : planPrice ? <strong>₹{planPrice.toLocaleString('en-IN')}</strong> : null}
          </span>
        </button>
        <button type="button" aria-label={`Actions for ${c.name}`} onClick={() => setAppActions(c)}
          style={{ width: 44, height: 44, flexShrink: 0, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-card)', color: 'var(--text-muted)', display: 'grid', placeItems: 'center', cursor: 'pointer' }}>
          <MoreHorizontal size={18} />
        </button>
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div className="customers-header mb-4">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>
              {filters.customerType === 'broadband' ? 'Broadband Customers'
                : filters.customerType === 'catv' ? 'CableTV Customers'
                : 'All Customers'}
            </h2>
            <p className="text-muted text-small">{pagination.total || 0} customers{filters.customerType && filters.customerType !== 'broadband' ? ` · ${filters.customerType}` : ''}</p>
          </div>
        </div>
        <div className="customers-actions">
          {isAdmin && selectedIds.size > 0 && (
            <>
              <button className="btn btn-secondary btn-sm" disabled={bulkStatusBusy} onClick={handleDeactivateSelected}>
                <PauseCircle size={14} /> Deactivate Selected ({selectedIds.size})
              </button>
              <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected}>
                <Trash2 size={14} /> Delete Selected ({selectedIds.size})
              </button>
            </>
          )}
          {filters.customerType === 'catv' && (() => {
            const activeAndPending = filters.status === 'active' && filters.balance === 'due';
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '.78rem', fontWeight: 600, color: activeAndPending ? 'var(--c-0284c7)' : 'var(--c-6b7280)' }}>
                  Active & Pending
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={activeAndPending}
                  title="Show only active customers with a pending amount"
                  onClick={() => setFilters(f => ({
                    ...f,
                    status: activeAndPending ? '' : 'active',
                    balance: activeAndPending ? '' : 'due',
                    page: 1,
                  }))}
                  style={{
                    position: 'relative', width: 36, height: 20, borderRadius: 999,
                    border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0,
                    background: activeAndPending ? 'var(--cb-0284c7)' : 'var(--cb-d1d5db)',
                    transition: 'background .2s',
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 2, left: activeAndPending ? 18 : 2,
                    width: 16, height: 16, borderRadius: '50%', background: 'var(--cb-ffffff)',
                    transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.3)',
                  }} />
                </button>
              </div>
            );
          })()}
          {/* App View moves these four into the Filters sheet (see below). */}
          {!isApp && (
            <>
              <button className="btn btn-secondary btn-sm" onClick={downloadCSV} title="Download as CSV/Excel">
                <Download size={14} /> Excel
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => downloadContacts('catv')} title={selectedIds.size ? `Google Contacts CSV for the ${selectedIds.size} selected CableTV customer(s)` : 'Google Contacts CSV for all active CableTV customers (LCV format)'}>
                <Phone size={14} /> CableTV Contacts
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => downloadContacts('broadband')} title={selectedIds.size ? `Google Contacts CSV for the ${selectedIds.size} selected Broadband customer(s)` : 'Google Contacts CSV for all active Broadband customers (AIS format)'}>
                <Phone size={14} /> Broadband Contacts
              </button>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/import')}>
                <Upload size={14} /> Import
              </button>
            </>
          )}
          <Link to="/customers/add" className="btn btn-primary btn-sm">
            <Plus size={14} /> Add Customer
          </Link>
        </div>
      </div>

      {/* Filters — web: the bar itself; App View: search + Filters button, bar opens in a sheet */}
      {isApp ? appTopRow : filterBar}

      {/* Table */}
      <div className="card">
        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : customers.length === 0 ? (
            <div className="empty-state">
              <Search size={32} />
              <h3>No customers found</h3>
              <p>Try changing your filters or add a new customer.</p>
            </div>
          ) : isApp ? (
            <div className="av-stagger">{sortedCustomers.map(appCard)}</div>
          ) : (
            <ResponsiveTable
              columns={columns}
              rows={sortedCustomers}
              rowKey={c => c._id}
              rowStyle={c => selectedIds.has(c._id) ? { background: 'var(--primary-light, #eff6ff)' } : {}}
            />
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="pagination">
            <span style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginRight: 8 }}>
              Page {pagination.page} of {pagination.pages} · {pagination.total} total
            </span>
            <button className="page-btn" disabled={!pagination.hasPrev} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>←</button>
            <button className="page-btn" disabled={!pagination.hasNext} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>→</button>
          </div>
        )}
      </div>

      {/* App View: Filters sheet — the same filter bar as the web view, plus the export/import buttons */}
      {isApp && filtersOpen && (
        <AppSheet title="Filters" onClose={() => setFiltersOpen(false)}>
          {filterBar}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
            <button className="btn btn-secondary btn-sm" onClick={downloadCSV}><Download size={14} /> Excel</button>
            <button className="btn btn-secondary btn-sm" onClick={() => downloadContacts('catv')}><Phone size={14} /> CableTV Contacts</button>
            <button className="btn btn-secondary btn-sm" onClick={() => downloadContacts('broadband')}><Phone size={14} /> Broadband Contacts</button>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/import')}><Upload size={14} /> Import</button>
          </div>
          <button className="btn btn-primary" onClick={() => setFiltersOpen(false)} style={{ width: '100%', minHeight: 46, marginTop: 12 }}>
            Show {pagination.total || 0} customers
          </button>
        </AppSheet>
      )}

      {/* App View: ⋯ action sheet for one customer — the same actions as the web row icons */}
      {isApp && appActions && (() => {
        const c = appActions;
        const { isBB, isCatv } = rowMeta(c);
        const onu = isBB && c.broadband?.username ? oltPortMap[c.broadband.username.toLowerCase()] : null;
        const close = () => setAppActions(null);
        return (
          <AppSheet title={c.name} onClose={close}>
            <SheetRow icon={Eye} label="Open customer" onClick={() => { close(); navigate(`/customers/${c._id}`, { state: { from: returnTo } }); }} />
            {isCatv && (
              <SheetRow icon={Wallet} label={`Collect payment${(c.outstandingBalance || 0) > 0 ? ` · ₹${c.outstandingBalance.toLocaleString('en-IN')} due` : ''}`}
                onClick={() => { close(); navigate(`/customers/${c._id}?tab=collect`, { state: { from: returnTo } }); }} />
            )}
            <SheetRow icon={Edit2} label="Quick edit" onClick={() => { close(); setEditCustomer(c); }} />
            {isBB && c.broadband?.username && (
              <SheetRow icon={Activity} label="Live bandwidth" onClick={() => { close(); setBwPopup({ username: c.broadband.username, name: c.name }); }} />
            )}
            {onu && <SheetRow icon={ExternalLink} label={`Hardware · PON ${onu.port}`} onClick={() => { close(); setPonPopup({ customer: c, onu }); }} />}
            {isCatv && canSuspendGtplActive && (c.catv?.setTopBoxSerial || c.catv?.accountNumber) && (
              <SheetRow icon={PauseCircle} label={c.catv?.gtplSuspended ? 'Activate on GTPL' : 'Suspend on GTPL'}
                disabled={togglingGtplId === c._id} onClick={() => { close(); handleToggleGtplSuspend(c); }} />
            )}
            <SheetRow icon={Phone} label={`Call ${c.mobile}`} onClick={() => { window.location.href = `tel:${c.mobile}`; }} />
            {isAdmin && <SheetRow icon={Trash2} label="Delete" danger onClick={() => { close(); handleDelete(c._id, c.name); }} />}
          </AppSheet>
        );
      })()}

      {/* Quick Edit Modal */}
      {editCustomer && (
        <QuickEditModal
          customer={editCustomer}
          areas={areas}
          onSave={() => { setEditCustomer(null); fetchCustomers(); }}
          onClose={() => setEditCustomer(null)}
        />
      )}

      {/* Live Bandwidth Popup */}
      {bwPopup && (
        <div onClick={() => setBwPopup(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--card-bg, #fff)', borderRadius: 12,
            width: '100%', maxWidth: 520, margin: '0 16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 18px', borderBottom: '1px solid var(--border)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Activity size={16} color="var(--accent)" />
                <span style={{ fontWeight: 700, fontSize: '.95rem' }}>Live Bandwidth</span>
                <span style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>— {bwPopup.name}</span>
              </div>
              <button onClick={() => setBwPopup(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', color: 'var(--text-muted)' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: 16 }}>
              <LiveBandwidthPanel username={bwPopup.username} />
            </div>
          </div>
        </div>
      )}

      {/* PON Hardware Popup */}
      {ponPopup && (
        <div onClick={() => setPonPopup(null)} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--cb-ffffff)', borderRadius: 12, width: '100%', maxWidth: 420, margin: '0 16px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--cb-e5e7eb)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: '.95rem' }}>Hardware Details</span>
                <span style={{ fontSize: '.8rem', color: 'var(--c-6b7280)' }}>— {ponPopup.customer.name}</span>
              </div>
              <button onClick={() => setPonPopup(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-9ca3af)' }}>
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '16px 18px', display: 'grid', gap: 10 }}>
              {[
                ['PON Port',           ponPopup.onu.port         != null ? `PON ${ponPopup.onu.port}` : null],
                ['Serial Number',      ponPopup.onu.serialNumber],
                ['Model',              ponPopup.onu.model],
                ['Status',             ponPopup.onu.status],
                ['Rx Power',           ponPopup.onu.rxPower      != null ? `${ponPopup.onu.rxPower} dBm` : null],
                ['Last Deregistered',  ponPopup.onu.lastDeregister],
                ['Deregister Reason',  ponPopup.onu.deregisterReason],
                ['PPPoE',              ponPopup.customer.broadband?.username],
              ].map(([label, val]) => val ? (
                <div key={label} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid var(--cb-f3f4f6)', paddingBottom: 8 }}>
                  <span style={{ width: 120, flexShrink: 0, fontSize: '.78rem', color: 'var(--c-6b7280)' }}>{label}</span>
                  <span style={{ fontFamily: label === 'Serial Number' || label === 'PPPoE' ? 'monospace' : 'inherit', fontWeight: 600, fontSize: '.85rem', color: label === 'Status' ? (ponPopup.onu.status === 'online' ? 'var(--c-15803d)' : 'var(--c-dc2626)') : 'var(--c-111111)' }}>
                    {String(val)}
                  </span>
                </div>
              ) : null)}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <Link to={`/customers/${ponPopup.customer._id}?tab=hardware`} onClick={() => setPonPopup(null)}
                  style={{ flex: 1, textAlign: 'center', padding: '7px 0', borderRadius: 6, background: 'var(--accent)', color: 'var(--c-ffffff)', fontWeight: 600, fontSize: '.83rem', textDecoration: 'none' }}>
                  Full Hardware Details
                </Link>
                <Link to={`/olt?serial=${encodeURIComponent(ponPopup.customer.broadband?.username || '')}`} onClick={() => setPonPopup(null)}
                  style={{ flex: 1, textAlign: 'center', padding: '7px 0', borderRadius: 6, background: 'var(--primary)', color: 'var(--c-ffffff)', fontWeight: 600, fontSize: '.83rem', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  <ExternalLink size={13} /> Open in OLT
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
